import { ArrowRight } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { MarketNowDto, MarketOutlookStateDto, ScanWindow } from 'shared';

import { WatchStarButton } from '../components/domain/WatchStarButton';
import { HorizonSelector } from '../components/ui/HorizonSelector';
import { useMarketNow } from '../features/marketConsole/queries';
import { formatSignedPct, SCAN_HORIZON_OPTIONS, STANCE_STYLES } from '../lib/marketConsoleFormat';
import { useReplayDate } from '../lib/useReplayDate';
import { cn } from '../lib/utils';

const OUTLOOK_LABELS: Record<MarketOutlookStateDto, string> = {
  CONFIRMED_UPTREND: 'Confirmed uptrend',
  UPTREND_UNDER_PRESSURE: 'Uptrend under pressure',
  MARKET_IN_CORRECTION: 'Market in correction',
};
const PRESSURE_LABELS = {
  BUYING_PRESSURE: 'Buying pressure',
  SELLING_PRESSURE: 'Selling pressure',
  MIXED: 'Mixed',
} as const;

function ratioTone(value: number | null): string {
  if (value === null) return 'text-text-muted';
  return value >= 1 ? 'text-success' : 'text-danger';
}

function changeTone(value: number | null): string {
  if (value === null || value === 0) return 'text-text-muted';
  return value > 0 ? 'text-success' : 'text-danger';
}

function t2108Read(value: number | null): string {
  if (value === null) return 'Not available';
  if (value < 20) return 'Washed out (below 20)';
  if (value > 70) return 'Stretched (above 70)';
  return 'Neutral zone (20–70)';
}

function formatChange(value: number | null, digits: number, suffix = ''): string {
  if (value === null) return '—';
  return `${value > 0 ? '+' : ''}${value.toFixed(digits)}${suffix}`;
}

function Tile({ label, value, note }: { label: string; value: ReactNode; note: string }) {
  return (
    <article className="rounded-lg border border-border bg-bg-muted p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
        {label}
      </div>
      <div className="mt-1 font-mono text-2xl font-semibold tabular-nums">{value}</div>
      <p className="mt-1 text-xs text-text-muted">{note}</p>
    </article>
  );
}

function DataDates({ data }: { data: MarketNowDto }) {
  const { dataDates } = data;
  return (
    <p className="text-xs text-text-muted">
      Stock close {dataDates.stockClose ?? '—'} → plan {data.planDate ?? '—'} · Breadth{' '}
      {dataDates.breadth ?? '—'} · ETFs {dataDates.etfClose ?? '—'} · QQQ outlook{' '}
      {dataDates.outlook ?? '—'} · Positioning {dataDates.positioning ?? '—'}
    </p>
  );
}

function ChartQueue({ data, horizon }: { data: MarketNowDto; horizon: ScanWindow }) {
  const { search } = useLocation();
  const queue = data.chartQueue?.[horizon] ?? [];
  return (
    <section
      className="rounded-lg border border-border bg-surface"
      aria-labelledby="now-queue-heading"
    >
      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <h2 id="now-queue-heading" className="text-sm font-semibold">
            Chart queue · {horizon}
          </h2>
          <p className="mt-0.5 text-xs text-text-muted">
            Tight coils first, then non-extended leaders from the strongest industries. Review the
            chart and write a plan before any trade.
          </p>
        </div>
        <Link
          to={{ pathname: '/market-console/stocks', search }}
          className="inline-flex shrink-0 items-center gap-1 text-xs underline"
        >
          Open Stocks <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      </div>
      {data.chartQueue === null ? (
        <p className="px-4 py-6 text-sm text-text-muted">No stock scan has run yet.</p>
      ) : queue.length === 0 ? (
        <p className="px-4 py-6 text-sm text-text-muted">
          Nothing qualifies in this horizon: no tight coils and no leaders in the top industries.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {queue.map(({ candidate, reason }) => (
            <li key={candidate.symbol} className="flex items-center gap-2 px-3 py-2">
              <WatchStarButton candidate={candidate} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{candidate.symbol}</div>
                <div className="truncate text-[11px] text-text-muted">
                  {candidate.industry} · {reason}
                </div>
              </div>
              <div className="text-right font-mono text-sm tabular-nums">
                <div className={changeTone(candidate.performance[horizon])}>
                  {formatSignedPct(candidate.performance[horizon])}
                </div>
                <div className="text-[11px] text-text-muted">
                  {candidate.atrExtension.toFixed(1)}× ATR over 50D
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function MarketNow() {
  const { search } = useLocation();
  const now = useMarketNow(useReplayDate().asOf);
  const [horizon, setHorizon] = useState<ScanWindow>('1M');

  if (now.isLoading) {
    return <main className="p-6 text-text-muted">Loading session plan…</main>;
  }
  if (now.isError || !now.data) {
    return <main className="p-6 text-danger">The session plan could not be loaded.</main>;
  }

  const data = now.data;
  const { stance, signals } = data;
  const style = STANCE_STYLES[stance.key];
  const themes = data.leadingThemes?.[horizon] ?? [];
  const maxTheme = Math.max(...themes.map((theme) => theme.medianPerformance), 0);

  return (
    <main className="text-text">
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-5 sm:px-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Now</h1>
          <DataDates data={data} />
        </header>

        <section
          className={cn('rounded-lg border border-l-4 border-border bg-surface p-4', style.border)}
          aria-labelledby="now-plan-heading"
        >
          <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
            Plan · {data.planDate ?? 'next session unknown'}
          </div>
          <h2 id="now-plan-heading" className={cn('mt-1 text-xl font-semibold', style.text)}>
            {stance.label}
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-text-muted">{stance.guidance}</p>
          <p className="mt-2 text-xs text-text-muted">
            QQQ outlook: {stance.outlook ? OUTLOOK_LABELS[stance.outlook] : 'not available'} ·
            Breadth: {stance.pressure ? PRESSURE_LABELS[stance.pressure] : 'not available'}
          </p>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Tile
              label="5D / 10D breadth"
              value={
                <>
                  <span className={ratioTone(signals.ratio5)}>
                    {signals.ratio5?.toFixed(2) ?? '—'}
                  </span>
                  <span className="text-text-muted"> / </span>
                  <span className={ratioTone(signals.ratio10)}>
                    {signals.ratio10?.toFixed(2) ?? '—'}
                  </span>
                </>
              }
              note="Above 1 favours buyers"
            />
            <Tile
              label="T2108"
              value={signals.t2108 === null ? '—' : `${signals.t2108.toFixed(0)}%`}
              note={`Stocks above 40-day average · ${t2108Read(signals.t2108)}`}
            />
            <Tile
              label={`Tight coils · ${horizon}`}
              value={data.tightCounts?.[horizon] ?? '—'}
              note="Non-extended leaders in a 3–5 day coil"
            />
            <Tile
              label="Crowded futures"
              value={signals.crowdedMarkets.length}
              note={
                signals.crowdedMarkets.length > 0
                  ? signals.crowdedMarkets.join(', ')
                  : 'No speculator extremes'
              }
            />
          </div>

          <details className="mt-4 text-xs text-text-muted">
            <summary className="cursor-pointer select-none">Signal logic and caveats</summary>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                The stance combines the QQQ outlook (Health) with the breadth read (Pulse).
                Confirmed uptrend with buying pressure is aggressive; correction with selling
                pressure is risk-off; mixed combinations are constructive or defensive.
              </li>
              <li>
                Breadth ratios divide stocks up 4%+ by stocks down 4%+ over 5 and 10 sessions. T2108
                is the share of stocks above their 40-day average; extremes are context, not timing
                signals.
              </li>
              <li>
                Tight coils and the chart queue come from the after-close stock scan. Crowded
                futures are large-speculator positioning at 3-year extremes.
              </li>
              <li>
                This is informational. It never changes trade-plan gate decisions; your rules do.
              </li>
            </ul>
          </details>
        </section>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
            Ranking horizon
          </span>
          <HorizonSelector
            label="Ranking horizon"
            options={SCAN_HORIZON_OPTIONS}
            value={horizon}
            onChange={setHorizon}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <section
            className="rounded-lg border border-border bg-surface p-4"
            aria-labelledby="now-themes-heading"
          >
            <div className="flex items-start justify-between gap-3">
              <h2 id="now-themes-heading" className="text-sm font-semibold">
                Leading themes · {horizon}
              </h2>
              <Link
                to={{ pathname: '/market-console/flow', search }}
                className="inline-flex items-center gap-1 text-xs underline"
              >
                Open Flow <ArrowRight className="h-3 w-3" aria-hidden="true" />
              </Link>
            </div>
            {themes.length === 0 ? (
              <p className="py-4 text-sm text-text-muted">No ETF ranking yet.</p>
            ) : (
              <ol className="mt-3 space-y-2">
                {themes.map((theme) => (
                  <li
                    key={theme.group}
                    className="grid grid-cols-[minmax(0,8rem)_1fr_auto] items-center gap-3 text-sm"
                  >
                    <span className="truncate">{theme.group}</span>
                    <span
                      className="h-1.5 overflow-hidden rounded-full bg-bg-muted"
                      aria-hidden="true"
                    >
                      <span
                        className="block h-full rounded-full bg-primary"
                        style={{
                          width: `${maxTheme > 0 ? Math.max(4, (Math.max(theme.medianPerformance, 0) / maxTheme) * 100) : 4}%`,
                        }}
                      />
                    </span>
                    <span
                      className={cn('font-mono tabular-nums', changeTone(theme.medianPerformance))}
                    >
                      {formatSignedPct(theme.medianPerformance)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <section
            className="rounded-lg border border-border bg-surface p-4"
            aria-labelledby="now-since-heading"
          >
            <div className="flex items-start justify-between gap-3">
              <h2 id="now-since-heading" className="text-sm font-semibold">
                Since prior close
                {data.sincePriorSession ? ` (${data.sincePriorSession.priorDate})` : ''}
              </h2>
              <Link
                to={{ pathname: '/market-console/pulse', search }}
                className="inline-flex items-center gap-1 text-xs underline"
              >
                Open Pulse <ArrowRight className="h-3 w-3" aria-hidden="true" />
              </Link>
            </div>
            {data.sincePriorSession ? (
              <dl className="mt-3 grid grid-cols-3 gap-2">
                {[
                  {
                    label: '±4% net',
                    value: data.sincePriorSession.net4Change,
                    text: formatChange(data.sincePriorSession.net4Change, 0),
                  },
                  {
                    label: 'T2108',
                    value: data.sincePriorSession.t2108Change,
                    text: formatChange(data.sincePriorSession.t2108Change, 1, ' pp'),
                  },
                  {
                    label: '10D ratio',
                    value: data.sincePriorSession.ratio10Change,
                    text: formatChange(data.sincePriorSession.ratio10Change, 2),
                  },
                ].map((item) => (
                  <div key={item.label} className="rounded bg-bg-muted p-2">
                    <dt className="text-[11px] text-text-muted">{item.label}</dt>
                    <dd
                      className={cn(
                        'mt-1 font-mono text-lg font-semibold tabular-nums',
                        changeTone(item.value),
                      )}
                    >
                      {item.text}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="py-4 text-sm text-text-muted">Breadth history is not available yet.</p>
            )}
          </section>
        </div>

        <ChartQueue data={data} horizon={horizon} />
      </div>
    </main>
  );
}
